import React, { Component } from 'react';
import dayjs from 'dayjs';

import services from '@C/Reports/_utils/data';
import { TableGrid } from '@C/TableGrid';

import { TankList } from '@C/Utils';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import SimpleReactValidator from 'simple-react-validator';

// ─── helpers ──────────────────────────────────────────────────────────────────

// Extract unique gun names (preserving first-seen order) from all result rows
const extractGunNames = (results) => {
  const seen = new Set();
  const names = [];
  results.forEach((row) => {
    (row.gun_reading || []).forEach((g) => {
      if (!seen.has(g.gun__name)) {
        seen.add(g.gun__name);
        names.push(g.gun__name);
      }
    });
  });
  return names;
};

// Flatten each API row so gun readings and variation are top-level keys
const transformRow = (row) => {
  const gunMap = {};
  (row.gun_reading || []).forEach((g) => {
    if (!gunMap[g.gun__name]) gunMap[g.gun__name] = [];
    gunMap[g.gun__name].push(g.previous_day__closing_reading);
  });
  return {
    ...row,
    ...gunMap,
    variation_daily: row.variation?.daily ?? 0,
    variation_cumulative: row.variation?.cumulative ?? 0,
  };
};

const getMonthlyReportColumns = (gunNames = []) => {
  const baseColumns = [
    {
      name: 'Date',
      key: 'time_stamp',
      sortable: true,
      type: 'date',
    },
    {
      name: 'Opening Stock',
      key: 'opening_stock',
    },
    {
      name: 'Closing Stock',
      key: 'closing_stock',
    },
    {
      name: 'Receipt / Purchase',
      key: 'purchase',
    },
    {
      name: 'Dip Sales',
      key: 'sales_by_dip',
    },
  ];

  const gunColumns = gunNames.map((name) => ({
    name,
    key: name,
    group: 'opening_reading',
    formatter(props) {
      if (Array.isArray(props)) {
        return <>{props.map((v) => v.toFixed(2)).join(' / ')}</>;
      }
      return <>-</>;
    },
  }));

  const summaryColumns = [
    {
      name: 'Testing',
      key: 'testing',
    },
    {
      name: 'Bowser Filling',
      key: 'bowser_filling',
    },
    {
      name: 'Gun Sale',
      key: 'sales_by_meter',
    },
    {
      name: 'Variation',
      key: 'variation_daily',
    },
    {
      name: 'Cumulative Variation',
      key: 'variation_cumulative',
    },
    {
      name: 'Cumulative Sale',
      key: 'cumulative_sale',
    },
  ];

  return [...baseColumns, ...gunColumns, ...summaryColumns];
};

class MonthlyReportTankWise extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slipList: [],
      selectedId: '',
      showModal: false,
      staff_id: '',
      start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      tank_id: '',
      slip_no: '',
      vehicle_number: '',
      loading: false,
      productList: this.props.products,
      tankList: this.props.tank,
      limit: 20,
      count: 0,
      month: dayjs().format('YYYY-MM'),
      gunNames: [],
    };
    this.validator = new SimpleReactValidator({
      autoForceUpdate: this,
      element: (message) => <div className="input-error">{message}</div>,
    });
  }

  componentDidMount() {
    // Report data only loads when Generate Report button is clicked
    // No automatic loading on mount
  }

  handleQuickSearch = (e) => {
    e.preventDefault();
    this.getMonthlyReport();
  };

  getMonthlyReport = () => {
    const { tank_id, start_date, end_date, limit } = this.state;
    this.setState({ loading: true });
    services
      .getMonthlyReport({
        tank_id,
        start_date,
        end_date,
        limit,
        offset: 1,
      })
      .then((response) => {
        const results = response.data.data.results || [];
        const gunNames = extractGunNames(results);
        const slipList = results.map(transformRow);
        this.setState({
          slipList,
          gunNames,
          count: response.data.data.count,
          loading: false,
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
      });
  };

  handleDateChange = (e) => {
    const start = `${e.target.value}-01`;
    const end = `${e.target.value}-31`;
    this.setState({
      month: e.target.value,
      start_date: start,
      end_date: end,
    });
  };

  handleTankChange = (e) => {
    this.setState({ tank_id: e.value });
  };

  resetFilter = () => {
    this.setState({
      slipList: [],
      gunNames: [],
      staff_id: '',
      start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      month: dayjs().format('YYYY-MM'),
      product_uuid: '',
      tank_id: '',
      loading: false,
      slip_number: '',
      limit: 20,
    });
    // update validator
    this.validator.hideMessages();
    this.forceUpdate();
  };

  submitForm = (e) => {
    e.preventDefault();
    if (!this.validator.allValid()) {
      this.validator.showMessages();
      this.forceUpdate();
      return;
    }
    this.getMonthlyReport();
  };

  render() {
    const { slipList, gunNames } = this.state;
    const columns = getMonthlyReportColumns(gunNames);

    return (
      <section className="widget full-width list-table inverted">
        <header>
          <h3>Monthly Sales Report - Tank Wise</h3>
        </header>
        <main>
          <div className="table-filters">
            <div className="filter-wrapper">
              <div className="filter-group">
                <label>Month:</label>
                {this.validator.message('month', this.state.month, 'required')}
                <input
                  className="cstm-date-input"
                  type="month"
                  value={this.state.month}
                  max={dayjs().format('YYYY-MM')}
                  name="start_date"
                  onChange={this.handleDateChange}
                />
              </div>
              <div className="filter-group">
                <label>Tank:</label>
                {this.validator.message('tank', this.state.tank_id, 'required')}
                <TankList
                  onChange={this.handleTankChange}
                  value={this.state.tank_id}
                  options={this.state.tankList}
                />
              </div>
              <div className="filter-group reset-filter">
                <button onClick={this.resetFilter}>Reset filters</button>
              </div>
              <div className="filter-group">
                <button
                  className={`submit ${this.state.loading ? 'loading' : ''}`}
                  type="submit"
                  onClick={this.submitForm}
                  disabled={this.state.loading}
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>
          <div className="table-wrapper">
            {(() => {
              if (this.state.loading) {
                return <DropLoaderLarge />;
              } else {
                return (
                  <TableGrid
                    col={columns}
                    row={slipList}
                    headerGroups={[
                      {
                        key: 'opening_reading',
                        name: 'Opening Reading',
                      },
                    ]}
                    offset={1}
                    count={this.state.count}
                    limit={this.state.limit}
                    pagination="disabled"
                    hideActions={true}
                    exportOptions={{
                      fileName: `Monthly Report Tank Wise- ${this.state.start_date} to ${this.state.end_date}`,
                      reportName: 'Monthly Report - Tank Wise',
                      reportDate: `Date - ${dayjs(this.state.start_date).format(
                        'D MMM YY',
                      )} to ${dayjs(this.state.end_date).format('D MMM YY')}`,
                      reportTotal: '',
                      reportQuantity: '',
                      reportSales: '',
                      reportTesting: '',
                      reportBowser: '',
                      reportCustomerName: '',
                      reportGst: '',
                      reportOpeningBalance: '',
                      reportClosingBalance: '',
                      reportSaleTotal: '',
                      reportTCSTotal: '',
                    }}
                  />
                );
              }
            })()}
          </div>
        </main>
      </section>
    );
  }
}

export { MonthlyReportTankWise };
